import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Download, Search, ChevronLeft, ChevronRight, 
  Calendar, Filter, X, ArrowRight, History,
  Database, Users, Briefcase, HardDrive, Settings,
  Link, Mail, UserCog, KeyRound, UserCheck,
  Plus, Minus, RefreshCw, Server,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateARG } from '../../lib/utils';

// ============ HELPER COMPONENTS ============

const SortIcon = ({ field, sortConfig }) => {
  if (sortConfig.field !== field) {
    return <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />;
  }
  return sortConfig.dir === 'asc' 
    ? <ArrowUp className="h-4 w-4 text-primary" />
    : <ArrowDown className="h-4 w-4 text-primary" />;
};

const ChangeTypeBadge = ({ type }) => {
  const config = {
    added: { bg: 'bg-green-500/20', text: 'text-green-400', icon: Plus, label: 'Agregado' },
    removed: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Minus, label: 'Removido' },
    modified: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: RefreshCw, label: 'Modificado' }
  };
  const c = config[type] || config.modified;
  const Icon = c.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
      <Icon className="h-3 w-3" />
      {c.label}
    </span>
  );
};

const FiltersBar = ({ filters, setFilters, placeholder = "Buscar..." }) => {
  const setDateRange = (days) => {
    setFilters(prev => ({
      ...prev,
      date_from: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0]
    }));
  };

  return (
    <Card className="card-subtle">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filtros:</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Input type="date" value={filters.date_from}
              onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
              className="h-9 w-36" />
            <span className="text-muted-foreground">a</span>
            <Input type="date" value={filters.date_to}
              onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
              className="h-9 w-36" />
          </div>
          <div className="flex items-center gap-1 border-l pl-3">
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(7)}>
              7 días
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(30)}>
              1 mes
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(90)}>
              3 meses
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder={placeholder} value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="h-9 w-48" />
          </div>
          {(filters.search || filters.change_type) && (
            <Button variant="ghost" size="sm" 
              onClick={() => setFilters(prev => ({ ...prev, search: '', change_type: '' }))}>
              <X className="h-4 w-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const StatsCards = ({ stats, filters, setFilters }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
    <div className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${!filters.change_type ? 'ring-2 ring-primary' : ''}`}
      onClick={() => setFilters(prev => ({ ...prev, change_type: '' }))}>
      <History className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value">{stats.total_changes}</div>
        <div className="stats-card-label">Total Cambios</div>
      </div>
    </div>
    <div className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${filters.change_type === 'added' ? 'ring-2 ring-green-500' : ''}`}
      onClick={() => setFilters(prev => ({ ...prev, change_type: prev.change_type === 'added' ? '' : 'added' }))}>
      <Plus className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value text-green-500">{stats.added}</div>
        <div className="stats-card-label">Agregados</div>
      </div>
    </div>
    <div className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${filters.change_type === 'removed' ? 'ring-2 ring-yellow-500' : ''}`}
      onClick={() => setFilters(prev => ({ ...prev, change_type: prev.change_type === 'removed' ? '' : 'removed' }))}>
      <Minus className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value text-yellow-500">{stats.removed}</div>
        <div className="stats-card-label">Removidos</div>
      </div>
    </div>
    <div className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-blue-500/50 ${filters.change_type === 'modified' ? 'ring-2 ring-blue-500' : ''}`}
      onClick={() => setFilters(prev => ({ ...prev, change_type: prev.change_type === 'modified' ? '' : 'modified' }))}>
      <RefreshCw className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value text-blue-500">{stats.modified}</div>
        <div className="stats-card-label">Modificados</div>
      </div>
    </div>
    <div className="stats-card flex items-center gap-3">
      <Server className="h-6 w-6 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
      <div>
        <div className="stats-card-value text-purple-500">{stats.entities_affected}</div>
        <div className="stats-card-label">Instancias</div>
      </div>
    </div>
  </div>
);

const PaginationBar = ({ data, onPageChange }) => (
  data.total > 0 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-muted-foreground">{data.total} cambios encontrados</div>
      {data.pages > 1 && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onPageChange(data.page - 1)} disabled={data.page === 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">{data.page} / {data.pages}</span>
          <Button variant="outline" size="sm" onClick={() => onPageChange(data.page + 1)} disabled={data.page === data.pages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  ) : null
);

const EmptyState = ({ error, icon: Icon = History }) => (
  <div className="text-center py-12 text-muted-foreground">
    <Icon className="h-12 w-12 mx-auto mb-3 opacity-50" />
    <p>No se encontraron cambios en el período seleccionado</p>
    <p className="text-sm mt-2">{error || 'Probá ampliando el rango de fechas'}</p>
  </div>
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
  </div>
);

// ============ GENERIC TAB CONTENT ============

const SqlDomainTab = ({ 
  domain, 
  domainLabel,
  icon: Icon,
  stats, 
  changes, 
  filters, 
  setFilters, 
  sort, 
  setSort,
  loading,
  onPageChange,
  onExport,
  exporting
}) => {
  const formatDate = (dateStr) => formatDateARG(dateStr);

  const handleSort = (field) => {
    setSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc'
    }));
  };

  return (
    <div className="space-y-6">
      <StatsCards stats={stats} filters={filters} setFilters={setFilters} />
      <FiltersBar filters={filters} setFilters={setFilters} placeholder={`Buscar instancia o ${domainLabel.toLowerCase()}...`} />
      
      <Card className="card-subtle overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <LoadingSpinner />
          ) : changes.items.length === 0 ? (
            <EmptyState error={changes.error} icon={Icon} />
          ) : (
            <div className="overflow-x-auto">
              <table className="table-subtle">
                <thead>
                  <tr>
                    <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('entity')}>
                      <div className="flex items-center gap-1">
                        INSTANCIA <SortIcon field="entity" sortConfig={sort} />
                      </div>
                    </th>
                    <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('tipo')}>
                      <div className="flex items-center gap-1">
                        TIPO <SortIcon field="tipo" sortConfig={sort} />
                      </div>
                    </th>
                    <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('item')}>
                      <div className="flex items-center gap-1">
                        {domainLabel.toUpperCase()} <SortIcon field="item" sortConfig={sort} />
                      </div>
                    </th>
                    <th>DETALLE</th>
                    <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('fecha')}>
                      <div className="flex items-center gap-1">
                        FECHA <SortIcon field="fecha" sortConfig={sort} />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {changes.items.map((item) => (
                    <tr key={item.id}>
                      <td className="font-mono text-primary">{item.entity}</td>
                      <td><ChangeTypeBadge type={item.change_type} /></td>
                      <td className="font-medium max-w-xs truncate" title={item.item_key}>
                        {item.item_key?.split('|')[0] || '-'}
                      </td>
                      <td>
                        {item.change_type === 'modified' ? (
                          <div className="inline-flex items-center gap-1.5 text-xs bg-muted/50 px-2 py-1 rounded">
                            <span className="font-medium">{item.field_changed}:</span>
                            <span className="text-muted-foreground line-through">{String(item.old_value ?? 'N/A')}</span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className="text-primary">{String(item.new_value ?? 'N/A')}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {item.data ? Object.keys(item.data).slice(0, 3).join(', ') + '...' : '-'}
                          </span>
                        )}
                      </td>
                      <td className="text-muted-foreground text-sm">{formatDate(item.detected_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <PaginationBar data={changes} onPageChange={onPageChange} />
    </div>
  );
};

// ============ MAIN COMPONENT ============

const SqlTraceabilityReport = () => {
  const [activeTab, setActiveTab] = useState('databases');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Overview
  const [overview, setOverview] = useState({});

  // Default dates
  const defaultDateFrom = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const defaultDateTo = new Date().toISOString().split('T')[0];

  // Tab configurations
  const tabs = [
    { key: 'databases', label: 'Databases', icon: Database, endpoint: 'databases' },
    { key: 'usuarios', label: 'Logins', icon: Users, endpoint: 'usuarios' },
    { key: 'jobs', label: 'Jobs', icon: Briefcase, endpoint: 'jobs' },
    { key: 'backups', label: 'Backups', icon: HardDrive, endpoint: 'backups' },
    { key: 'config', label: 'Config', icon: Settings, endpoint: 'config' },
    { key: 'linkedservers', label: 'Linked Srv', icon: Link, endpoint: 'linkedservers' },
    { key: 'dbmail', label: 'DB Mail', icon: Mail, endpoint: 'dbmail' },
    { key: 'operators', label: 'Operators', icon: UserCog, endpoint: 'operators' },
    { key: 'credentials', label: 'Credentials', icon: KeyRound, endpoint: 'credentials' },
    { key: 'db_usuarios', label: 'DB Users', icon: UserCheck, endpoint: 'db-usuarios' },
  ];

  // State for each tab (using a single object to reduce code)
  const [tabStates, setTabStates] = useState(() => {
    const initial = {};
    tabs.forEach(t => {
      initial[t.key] = {
        stats: { total_changes: 0, added: 0, removed: 0, modified: 0, entities_affected: 0 },
        changes: { items: [], total: 0, page: 1, pages: 1 },
        filters: { date_from: defaultDateFrom, date_to: defaultDateTo, change_type: '', search: '' },
        sort: { field: 'fecha', dir: 'desc' }
      };
    });
    return initial;
  });

  // Helper to update tab state
  const updateTabState = (tabKey, field, value) => {
    setTabStates(prev => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        [field]: typeof value === 'function' ? value(prev[tabKey][field]) : value
      }
    }));
  };

  // Fetch overview
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const response = await api.get('/reports/sql-traceability/overview');
        setOverview(response.data);
      } catch (error) {
        console.error('Error fetching overview:', error);
      }
    };
    fetchOverview();
  }, []);

  // Fetch stats for active tab
  const fetchStats = useCallback(async (tabKey) => {
    const tab = tabs.find(t => t.key === tabKey);
    if (!tab) return;
    
    const state = tabStates[tabKey];
    try {
      const params = new URLSearchParams();
      if (state.filters.date_from) params.append('date_from', state.filters.date_from);
      if (state.filters.date_to) params.append('date_to', state.filters.date_to);
      const response = await api.get(`/reports/sql-traceability/${tab.endpoint}/stats?${params}`);
      updateTabState(tabKey, 'stats', response.data);
    } catch (error) {
      console.error(`Error fetching ${tabKey} stats:`, error);
    }
  }, [tabStates]);

  // Fetch changes for active tab
  const fetchChanges = useCallback(async (tabKey, page = 1) => {
    const tab = tabs.find(t => t.key === tabKey);
    if (!tab) return;
    
    setLoading(true);
    const state = tabStates[tabKey];
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50' });
      if (state.filters.date_from) params.append('date_from', state.filters.date_from);
      if (state.filters.date_to) params.append('date_to', state.filters.date_to);
      if (state.filters.change_type) params.append('change_type', state.filters.change_type);
      if (state.filters.search) params.append('search', state.filters.search);
      params.append('sort_by', state.sort.field);
      params.append('sort_dir', state.sort.dir);
      
      const response = await api.get(`/reports/sql-traceability/${tab.endpoint}/changes?${params}`);
      updateTabState(tabKey, 'changes', response.data);
    } catch (error) {
      console.error(`Error fetching ${tabKey} changes:`, error);
    } finally {
      setLoading(false);
    }
  }, [tabStates]);

  // Effect: when tab or filters change, fetch data
  useEffect(() => {
    fetchStats(activeTab);
    fetchChanges(activeTab, 1);
  }, [activeTab, tabStates[activeTab]?.filters, tabStates[activeTab]?.sort]);

  // Export handler
  const handleExport = async () => {
    const tab = tabs.find(t => t.key === activeTab);
    if (!tab) return;
    
    setExporting(true);
    try {
      const state = tabStates[activeTab];
      const params = new URLSearchParams();
      if (state.filters.date_from) params.append('date_from', state.filters.date_from);
      if (state.filters.date_to) params.append('date_to', state.filters.date_to);
      
      const response = await api.get(`/reports/sql-traceability/${tab.endpoint}/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trazabilidad_sql_${tab.key}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Get domain key for overview (sql_databases, sql_usuarios, etc)
  const getDomainKey = (tabKey) => {
    if (tabKey === 'db_usuarios') return 'sql_db_usuarios';
    return `sql_${tabKey}`;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Trazabilidad SQL Server</h1>
            <p className="page-subtitle">Control de cambios de Databases, Logins, Jobs, Backups, Config y más</p>
          </div>
          <Button onClick={handleExport} disabled={exporting} variant="outline" data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-10">
            {tabs.map(tab => {
              const domainKey = getDomainKey(tab.key);
              const weekChanges = overview[domainKey]?.changes_week || 0;
              return (
                <TabsTrigger key={tab.key} value={tab.key} data-testid={`tab-${tab.key}`} className="text-xs">
                  <tab.icon className="h-4 w-4 mr-1" />
                  <span className="hidden xl:inline">{tab.label}</span>
                  {weekChanges > 0 && (
                    <span className="ml-1 text-xs bg-primary/20 text-primary px-1 py-0.5 rounded">{weekChanges}</span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Tab Contents */}
          {tabs.map(tab => (
            <TabsContent key={tab.key} value={tab.key}>
              <SqlDomainTab
                domain={tab.key}
                domainLabel={tab.label}
                icon={tab.icon}
                stats={tabStates[tab.key].stats}
                changes={tabStates[tab.key].changes}
                filters={tabStates[tab.key].filters}
                setFilters={(updater) => updateTabState(tab.key, 'filters', updater)}
                sort={tabStates[tab.key].sort}
                setSort={(updater) => updateTabState(tab.key, 'sort', updater)}
                loading={loading}
                onPageChange={(page) => fetchChanges(tab.key, page)}
                onExport={handleExport}
                exporting={exporting}
              />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
};

export default SqlTraceabilityReport;
